// ── Tool View ("/tools") — FR-TOOL-001 (SUPER-ADMIN, READ-ONLY, LIVE) ───────
// GET /tools/diagnostics is NOW LIVE and SUPER-ADMIN ONLY (coaching.tools.view.org,
// migration 0227, plus a backend defense-in-depth role==='super_admin' check). It
// renders the operational diagnostics: provider-gate status (each lane OPEN/CLOSED
// + live | dry_run mode), outbox health (counts by status and by provider mode),
// and config flags (the *_LIVE_ENABLED booleans + a configured / not-configured
// indicator per secret-bearing var).
//
// ── NO SECRET LEAK (release gate) ────────────────────────────────────────────
// The response carries NO secret values — only booleans, gate states, and counts
// (secretsRedacted:true). This UI renders the per-secret status ONLY as a neutral
// "configured" / "not configured" badge and NEVER attempts to show a value.
//
// ── ACCESS (FR-TOOL-001) ─────────────────────────────────────────────────────
// SA-reachable ONLY. Every other role is denied at the route AND here (fail-closed);
// the diagnostics read is `enabled`-gated so it NEVER fires for a non-super-admin.

import { Badge, Module, ModuleBody, ModuleHead, PageFrame } from "@vjal/ui";

import { HonestState } from "../../components/honest-state";
import { useToolViewDiagnostics } from "../../hooks/useToolViewDiagnostics";
import { canAccessToolView } from "../../domain/logs-and-exports";
import type { ProductScreenContext } from "../product-route-contracts";
import {
  ToolViewConfigFlags,
  ToolViewOutboxHealth,
  ToolViewProviderGates,
} from "./tool-view-sections";

export function ToolViewPage({ role }: ProductScreenContext) {
  const isSuperAdmin = canAccessToolView(role);
  // The read is gated on SA so it NEVER fires for a denied role (fail-closed).
  const diagnostics = useToolViewDiagnostics(isSuperAdmin);

  if (!isSuperAdmin) {
    return (
      <section aria-label="Tool View" className="col-span-12" data-testid="tool-view-page">
        <div className="rounded-xl border border-line bg-white">
          <HonestState
            posture="permission-denied"
            title="Tool View is restricted"
            message="Only super-admins can open operational diagnostics."
          />
        </div>
      </section>
    );
  }

  const { posture, data, gate, reference, retry } = diagnostics;

  return (
    /* F3: Page-1 shell. The shared PageHeader owns the page's primary heading. */
    <PageFrame
      title="Tool View"
      subtitle="Super-admin operational diagnostics — read-only. No secret values are shown here."
      className="col-span-12"
    >
      <section aria-label="Tool View" className="flex flex-col gap-4" data-testid="tool-view-page">
        <Module span={12}>
          <ModuleHead
            title="Diagnostics shell"
            subtitle="Provider gates, outbox health, and config flags."
          />
          <ModuleBody className="space-y-4" data-testid="tool-view-shell">
            <Badge variant="default">Read-only diagnostics — no secrets</Badge>
            {posture ? (
              <HonestState
                posture={posture}
                gate={gate}
                reference={reference}
                onRetry={retry}
                title={posture === "permission-denied" ? "Tool View is restricted" : undefined}
              />
            ) : data ? (
              <div className="space-y-4" data-testid="tool-view-diagnostics">
                <p className="m-0 text-xs text-ink-muted">Generated {data.generatedAt}</p>
                <ToolViewProviderGates lanes={data.providerGates} />
                <ToolViewOutboxHealth outbox={data.outbox} />
                <ToolViewConfigFlags configFlags={data.configFlags} secretsRedacted={data.secretsRedacted} />
              </div>
            ) : null}
          </ModuleBody>
        </Module>
      </section>
    </PageFrame>
  );
}
